import React, { useCallback, useMemo, useState } from 'react';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useLocale } from '../i18n/LocaleContext';
import { useVisualTheme } from '../theme/VisualThemeContext';
import { chatListPalette } from '../theme/chatListPalette';
import { DEFAULT_AVATAR_SHAPE, normalizeAvatarShapeId, type AvatarShapeId } from '../avatar/shapeCatalog';
import { AvatarEditorPanel } from './AvatarEditorPanel';

type Props = {
  chatId: string;
  displayName: string;
  initialHasAvatar: boolean;
  initialRevision: number;
  initialShape?: string | null;
  canEdit: boolean;
  onApplied: (next: {
    chatHasAvatar: boolean;
    chatAvatarRevision: number;
    chatAvatarShape: string | null;
  }) => void;
};

/**
 * Фото чата. Тонкая обёртка над `AvatarEditorPanel`: вся работа со снимком — выбор источника,
 * кадрирование жестами, загрузка, удаление — общая с фото профиля.
 *
 * <p>Форма аватара здесь не редактируется: у чата она приходит с сервера вместе с чатом, и
 * менять её из этого окна никогда было нельзя. Поэтому `onShapeChange` не передаётся, и
 * полоска форм не появляется — маска в редакторе просто берёт форму чата.
 */
export function ChatAvatarEditor({
  chatId,
  displayName,
  initialHasAvatar,
  initialRevision,
  initialShape,
  canEdit,
  onApplied,
}: Props): React.ReactElement {
  const { t } = useLocale();
  const { resolvedVisualTheme } = useVisualTheme();
  const pal = useMemo(() => chatListPalette(resolvedVisualTheme), [resolvedVisualTheme]);
  const shape: AvatarShapeId = normalizeAvatarShapeId(initialShape ?? DEFAULT_AVATAR_SHAPE);
  const [hasAvatar, setHasAvatar] = useState(initialHasAvatar);
  const [revision, setRevision] = useState(initialRevision);

  // Сервер прислал другой аватар чата — показываем его, даже если здесь уже что-то меняли.
  const [prevInitial, setPrevInitial] = useState({ hasAvatar: initialHasAvatar, revision: initialRevision });
  if (prevInitial.hasAvatar !== initialHasAvatar || prevInitial.revision !== initialRevision) {
    setPrevInitial({ hasAvatar: initialHasAvatar, revision: initialRevision });
    setHasAvatar(initialHasAvatar);
    setRevision(initialRevision);
  }

  const onUploaded = useCallback(
    (avatarRevision: number) => {
      setHasAvatar(true);
      setRevision(avatarRevision);
      onApplied({ chatHasAvatar: true, chatAvatarRevision: avatarRevision, chatAvatarShape: shape });
    },
    [onApplied, shape],
  );

  const onRemoved = useCallback(
    (avatarRevision: number) => {
      setHasAvatar(false);
      setRevision(avatarRevision);
      onApplied({ chatHasAvatar: false, chatAvatarRevision: avatarRevision, chatAvatarShape: shape });
    },
    [onApplied, shape],
  );

  if (!canEdit) {
    return (
      <View style={styles.block}>
        <AvatarEditorPanel
          target={{ kind: 'chat', chatId }}
          displayName={displayName}
          hasAvatar={hasAvatar}
          revision={revision}
          shapeId={shape}
          title={t('chatAvatarSectionTitle')}
          canEdit={false}
          onUploaded={onUploaded}
          onRemoved={onRemoved}
          badgeSize={88}
        />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.scroll}
      contentContainerStyle={styles.scrollInner}
      keyboardShouldPersistTaps="handled">
      <Text style={[styles.sectionTitle, { color: pal.settingsSectionTitle }]}>
        {t('chatAvatarSectionTitle')}
      </Text>

      <AvatarEditorPanel
        target={{ kind: 'chat', chatId }}
        displayName={displayName}
        hasAvatar={hasAvatar}
        revision={revision}
        shapeId={shape}
        title={t('chatAvatarSectionTitle')}
        onUploaded={onUploaded}
        onRemoved={onRemoved}
      />

      {Platform.OS === 'web' ? (
        <Text style={[styles.muted, { color: pal.settingsFieldLabel }]}>
          {t('settingsAvatarWebHint')}
        </Text>
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  block: { paddingVertical: 8 },
  scroll: { maxHeight: 520 },
  scrollInner: { paddingBottom: 12, gap: 10 },
  sectionTitle: { fontSize: 15, fontWeight: '700', marginBottom: 4 },
  muted: { fontSize: 12 },
});
